// Ch94 · SOCIALS FILTERS — type, building, and new-vs-revised.
//
// 🔴 THE OPTION SET IS OPEN-ENDED. DERIVE IT; NEVER HARDCODE IT.
//
// `template` is free text with a length-only CHECK, and — settled by Agent 287
// on 2026-09-19 after retracting an earlier read — a plan's `format_slug` FALLS
// THROUGH to it: `options.template ?? plan.format_slug ?? "unclassified"`
// (assembler/contentLineCore.ts:540). So ANY new format_slug becomes a template
// with no code change and no migration.
//
// What that cost, concretely: the approved mock's chips read
// Tour · Explainer · Lawless · Budget. Against prod that offers two types with
// ZERO cards (lawless, budget) and hides `service_explainer` — 13 of 63 — plus
// `url-phone-social` (5) and `property-reel` (4), the last two UNSOURCED: they
// appear nowhere in origin/main's history, because the box runs a pinned
// release a local clone cannot speak for. A hardcoded list silently loses 22 of
// 63 cards. The chips are a mock, not a schema.
//
// 🔴 TWO FALLBACKS THAT MEAN DIFFERENT THINGS, and neither is a type:
//   "unclassified"  the PLAN named no template and no format_slug   (:540)
//   "unknown"       the META carried no template            (reviewCockpit:822)
// Both mean "this builder did not say". Surfacing either as a type would invent
// a category, so they are grouped under one honest label that keeps the raw
// value for the behind-the-scenes view.
//
// No React, so every rule is testable without a DOM.

import { buildingKeyOf, type NameableReel } from "./reelIdentity";

export interface FilterableReel extends NameableReel {
  template: string;
  status: string;
  revision_number?: number;
  parent_reel_id?: string | null;
}

/** The two values that mean "no type was recorded", not a type. */
const NOT_RECORDED = new Set(["unknown", "unclassified"]);

/**
 * Whether this card actually states a type.
 *
 * Exported so the TILE and the FILTER cannot drift apart: the filter grouping
 * these two under "Type not recorded" while a tile prints "Unclassified" as a
 * chip would be the same card claiming two different things on one screen.
 */
export const isTypeRecorded = (template: string): boolean => {
  const raw = (template || "").trim();
  return Boolean(raw) && !NOT_RECORDED.has(raw);
};

export const TYPE_NOT_RECORDED = "__not_recorded__";

export interface FilterOption {
  /** The value to match on, or TYPE_NOT_RECORDED for the grouped silences. */
  value: string;
  /** What Grant reads. */
  label: string;
  count: number;
  /** The raw values folded into this option — for the behind-the-scenes view. */
  rawValues?: string[];
}

/**
 * Names for the templates we have actually seen, so the UI does not print raw
 * slugs at Grant. "Url phone social" is a machine's word; his mock says the
 * types in English.
 *
 * ⚠️ A LOOKUP, NOT AN ALLOW-LIST. The vocabulary is open-ended (a new
 * format_slug becomes a template with no code change), so anything unknown
 * still renders — cleaned up, never dropped. That is the same rule the filter
 * options follow.
 */
const TEMPLATE_NAMES: Record<string, string> = {
  property_tour: "Property tour",
  "property-reel": "Property reel",
  service_explainer: "How it works",
  "url-phone-social": "Call us",
  lawless: "Lawless",
  deal_promo: "Deal",
  "data-drop": "Data drop",
  listicle: "Listicle",
  budget_reel: "Budget",
  what_x_gets_you: "What $X gets you",
  amenity_roundup: "Amenities",
  hidden_gem_near_apartment: "Hidden gem",
  property_montage: "Montage",
  budget_comparison: "Budget comparison",
  "neighborhood-guide": "Neighbourhood guide",
  luxury_showcase: "Luxury",
};

/** "property_tour" → "Property tour". Handles BOTH naming conventions. */
export function humaniseTemplate(template: string): string {
  const known = TEMPLATE_NAMES[(template || "").trim()];
  if (known) return known;
  // The vocabulary is not internally consistent: `property_tour` sits beside
  // `url-phone-social` and `property-reel`. A split on one separator drops the
  // other, so both are treated as word breaks.
  const words = template.replace(/[_-]+/g, " ").trim();
  if (!words) return "Not recorded";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Build the type options actually present in the loaded page.
 *
 * Sorted by count, descending, so the types Grant sees most are first — with
 * "not recorded" always last, because it is a silence rather than a category.
 */
export function deriveTypeOptions(reels: FilterableReel[]): FilterOption[] {
  const counts = new Map<string, number>();
  const rawUnderNotRecorded = new Set<string>();

  reels.forEach((reel) => {
    const raw = (reel.template || "").trim();
    if (!raw || NOT_RECORDED.has(raw)) {
      rawUnderNotRecorded.add(raw || "(empty)");
      counts.set(TYPE_NOT_RECORDED, (counts.get(TYPE_NOT_RECORDED) ?? 0) + 1);
      return;
    }
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  });

  const options: FilterOption[] = [];
  counts.forEach((count, value) => {
    if (value === TYPE_NOT_RECORDED) return;
    options.push({ value, label: humaniseTemplate(value), count });
  });
  options.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const notRecorded = counts.get(TYPE_NOT_RECORDED);
  if (notRecorded) {
    options.push({
      value: TYPE_NOT_RECORDED,
      label: "Type not recorded",
      count: notRecorded,
      rawValues: Array.from(rawUnderNotRecorded).sort(),
    });
  }
  return options;
}

/**
 * Building options.
 *
 * A card whose `property_name` is ABSENT is not a building called "Unknown" —
 * it is a card rendered before the builder populated the field, and it is
 * otherwise fine. It gets its own option rather than being dropped, because
 * dropping it hides real work from the person meant to review it.
 */
export const BUILDING_NOT_RECORDED = "__no_building__";

export function deriveBuildingOptions(reels: FilterableReel[]): FilterOption[] {
  const counts = new Map<string, number>();
  reels.forEach((reel) => {
    // buildingKeyOf returns null only when we genuinely cannot tell — it never
    // invents a building from a slug that cleans down to nothing.
    const name = buildingKeyOf(reel);
    counts.set(
      name ?? BUILDING_NOT_RECORDED,
      (counts.get(name ?? BUILDING_NOT_RECORDED) ?? 0) + 1
    );
  });

  const options: FilterOption[] = [];
  counts.forEach((count, value) => {
    if (value === BUILDING_NOT_RECORDED) return;
    options.push({ value, label: value, count });
  });
  options.sort((a, b) => a.label.localeCompare(b.label));

  const missing = counts.get(BUILDING_NOT_RECORDED);
  if (missing) {
    options.push({
      value: BUILDING_NOT_RECORDED,
      label: "Building not recorded",
      count: missing,
    });
  }
  return options;
}

export type FreshnessFilter = "all" | "new" | "revised";

/**
 * New vs "revised from my note".
 *
 * A reel is revised when it descends from another one OR carries a revision
 * number above the translator's default of 0. `revision_number` is ALWAYS
 * present on the DTO and defaults to 0, so a 0 here means "first cut", not
 * "unknown" — one of the few places absence is genuinely decided upstream.
 */
export function isRevised(reel: FilterableReel): boolean {
  return Boolean(reel.parent_reel_id) || (reel.revision_number ?? 0) > 1;
}

export interface SocialsFilterState {
  type: string | null;
  building: string | null;
  freshness: FreshnessFilter;
}

export const NO_FILTERS: SocialsFilterState = {
  type: null,
  building: null,
  freshness: "all",
};

export function applyFilters<T extends FilterableReel>(
  reels: T[],
  filters: SocialsFilterState
): T[] {
  return reels.filter((reel) => {
    if (filters.type) {
      const raw = (reel.template || "").trim();
      const isNotRecorded = !raw || NOT_RECORDED.has(raw);
      if (filters.type === TYPE_NOT_RECORDED) {
        if (!isNotRecorded) return false;
      } else if (raw !== filters.type) {
        return false;
      }
    }

    if (filters.building) {
      const name = buildingKeyOf(reel);
      if (filters.building === BUILDING_NOT_RECORDED) {
        if (name) return false;
      } else if (name !== filters.building) {
        return false;
      }
    }

    if (filters.freshness === "new" && isRevised(reel)) return false;
    if (filters.freshness === "revised" && !isRevised(reel)) return false;

    return true;
  });
}

export const hasActiveFilter = (filters: SocialsFilterState): boolean =>
  Boolean(filters.type || filters.building) || filters.freshness !== "all";

/**
 * The line under the filter strip.
 *
 * 🔴 It says what the filter DESCRIBES, not what exists. `contentReviewService`
 * loads 24 items with no pagination against a 29-card queue, so options are
 * derived from ~83% of it and a type whose only cards are further down does not
 * exist as far as this strip is concerned. Saying "of 29" while filtering 24
 * would be a quiet lie; naming the loaded set is the honest version, and
 * Phase 1's pagination shrinks the gap rather than hiding it.
 */
export function filterSummary(
  shown: number,
  loaded: number,
  totalKnown: number | null
): string {
  const base = `Showing ${shown} of ${loaded} loaded`;
  if (totalKnown !== null && totalKnown > loaded) {
    return `${base} · ${totalKnown} in the queue`;
  }
  return base;
}
